import React from 'react'
import './detail.css'
import { onClickableTableRow } from '../../../utils/tableRowClick'
import { useClientPagination } from '../../../hooks/useClientPagination'
import TablePager from '../TablePager'
import TableLoadingState from '../TableLoadingState'

type DetailPageHeadProps = {
  eyebrow: string
  title: string
  subtitle?: string
  actions?: React.ReactNode
  titleTourId?: string
}

export function DetailPageHead({
  eyebrow,
  title,
  subtitle,
  actions,
  titleTourId,
}: DetailPageHeadProps) {
  return (
    <div className="detail-page-head">
      <div>
        <p className="detail-eyebrow">{eyebrow}</p>
        <h1 className="detail-title" data-tour-id={titleTourId}>
          {title}
        </h1>
        {subtitle ? <p className="detail-sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="detail-head-actions">{actions}</div> : null}
    </div>
  )
}

type DetailButtonProps = {
  children: React.ReactNode
  onClick?: () => void
  variant?: 'primary' | 'ghost' | 'danger'
  size?: 'md' | 'sm'
  type?: 'button' | 'submit'
  className?: string
  disabled?: boolean
  'data-tour-id'?: string
}

export function DetailButton({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  type = 'button',
  className = '',
  disabled = false,
  'data-tour-id': dataTourId,
}: DetailButtonProps) {
  const classes = [
    'detail-btn',
    variant === 'ghost' ? 'detail-btn--ghost' : '',
    variant === 'danger' ? 'detail-btn--danger' : '',
    size === 'sm' ? 'detail-btn--sm' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled}
      data-tour-id={dataTourId}
    >
      {children}
    </button>
  )
}

type DetailKpiCardProps = {
  label: string
  value: React.ReactNode
  description?: string
  valueTone?: 'default' | 'coral'
  valueSize?: 'md' | 'sm'
}

export function DetailKpiCard({
  label,
  value,
  description,
  valueTone = 'default',
  valueSize = 'md',
}: DetailKpiCardProps) {
  const valueClass = [
    'detail-kpi-value',
    valueTone === 'coral' ? 'detail-kpi-value--coral' : '',
    valueSize === 'sm' ? 'detail-kpi-value--sm' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="detail-kpi">
      <div className="detail-eyebrow" title={label}>
        {label}
      </div>
      <div className={valueClass}>{value}</div>
      <div
        className={`detail-kpi-desc${description ? '' : ' is-empty'}`}
      >
        {description || '\u00a0'}
      </div>
    </div>
  )
}

type DetailKpiGridProps = {
  children: React.ReactNode
  /** Number of columns on desktop (default 4). Use 3 for dense analysis grids. */
  columns?: 2 | 3 | 4 | 5 | 6
}

export function DetailKpiGrid({
  children,
  columns = 4,
}: DetailKpiGridProps) {
  return (
    <div
      className={`detail-kpi-grid detail-kpi-grid--${columns}`}
    >
      {children}
    </div>
  )
}

type DetailAccordionProps = {
  title: string
  count?: number | string | null
  open: boolean
  onToggle: () => void
  children: React.ReactNode
  headerAction?: React.ReactNode
}

export function DetailAccordion({
  title,
  count,
  open,
  onToggle,
  children,
  headerAction,
}: DetailAccordionProps) {
  return (
    <div className={`detail-acc${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="detail-acc-head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <svg
          className="detail-acc-chev"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden
        >
          <path d="M6 3l5 5-5 5" />
        </svg>
        <h3 className="detail-acc-title">{title}</h3>
        {count != null && count !== '' ? (
          <span className="detail-acc-count">{count}</span>
        ) : null}
        <span className="detail-acc-spacer" />
        {headerAction ? (
          <span
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {headerAction}
          </span>
        ) : null}
      </button>
      <div className="detail-acc-body">{children}</div>
    </div>
  )
}

type DetailInfoItemProps = {
  label: string
  value: React.ReactNode
  mono?: boolean
}

export function DetailInfoItem({ label, value, mono }: DetailInfoItemProps) {
  return (
    <div className="detail-info-item">
      <div className="detail-eyebrow">{label}</div>
      <div
        className={`detail-info-val${mono ? ' detail-info-val--mono' : ''}`}
      >
        {value ?? '—'}
      </div>
    </div>
  )
}

type DetailInfoGridProps = {
  children: React.ReactNode
  /** `stack` = one field per row (narrow overview panels). Default wraps to multi-column. */
  variant?: 'grid' | 'stack'
}

export function DetailInfoGrid({
  children,
  variant = 'grid',
}: DetailInfoGridProps) {
  return (
    <div className="detail-acc-inner">
      <div
        className={
          variant === 'stack' ? 'detail-info-stack' : 'detail-info-grid'
        }
      >
        {children}
      </div>
    </div>
  )
}

type DetailPanelProps = {
  title: string
  subtitle?: string
  children: React.ReactNode
  className?: string
}

/** Card panel for detail dashboards (overview, description, etc.). */
export function DetailPanel({
  title,
  subtitle,
  children,
  className = '',
}: DetailPanelProps) {
  return (
    <section className={`detail-panel ${className}`.trim()}>
      <header className="detail-panel-head">
        <div>
          <h3 className="detail-panel-title">{title}</h3>
          {subtitle ? <p className="detail-panel-sub">{subtitle}</p> : null}
        </div>
      </header>
      <div className="detail-panel-body">{children}</div>
    </section>
  )
}

type DetailProseProps = {
  text: string
  emptyLabel?: string
}

/**
 * Renders long-form structured text with readable paragraphs / list lines.
 * Expects text already prepared (e.g. via formatStructuredDescription).
 */
export function DetailProse({
  text,
  emptyLabel = '—',
}: DetailProseProps) {
  const trimmed = text?.trim()
  if (!trimmed) {
    return <p className="detail-prose-empty">{emptyLabel}</p>
  }

  const blocks = trimmed
    .split(/\n{2,}/)
    .map((block) => block.trim())
    .filter(Boolean)

  return (
    <div className="detail-prose">
      {blocks.map((block, index) => {
        const lines = block.split('\n').map((l) => l.trim()).filter(Boolean)
        const isList =
          lines.length > 1 &&
          lines.every((line) => /^([a-z]|[A-Z]|\d{1,3})\)\s/.test(line))

        if (isList) {
          return (
            <ul key={index} className="detail-prose-list">
              {lines.map((line, lineIndex) => (
                <li key={lineIndex}>{line}</li>
              ))}
            </ul>
          )
        }

        if (lines.length > 1) {
          return (
            <p key={index} className="detail-prose-p">
              {lines.map((line, lineIndex) => (
                <React.Fragment key={lineIndex}>
                  {lineIndex > 0 ? <br /> : null}
                  {line}
                </React.Fragment>
              ))}
            </p>
          )
        }

        return (
          <p key={index} className="detail-prose-p">
            {block}
          </p>
        )
      })}
    </div>
  )
}

type DetailDashGridProps = {
  children: React.ReactNode
}

/** Two-column dashboard composition under KPI cards. */
export function DetailDashGrid({ children }: DetailDashGridProps) {
  return <div className="detail-dash-grid">{children}</div>
}

type DetailSectionBarProps = {
  title: string
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  action?: React.ReactNode
}

export function DetailSectionBar({
  title,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  action,
}: DetailSectionBarProps) {
  return (
    <div className="detail-sec-bar">
      <h4>{title}</h4>
      {onSearchChange ? (
        <label className="detail-field-search">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden>
            <circle cx="7" cy="7" r="4.5" />
            <path d="M10.5 10.5L14 14" />
          </svg>
          <input
            value={searchValue ?? ''}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder || 'Filter by name'}
          />
        </label>
      ) : null}
      {action}
    </div>
  )
}

type DetailEmptyStateProps = {
  title?: string
  message: string
  action?: React.ReactNode
}

export function DetailEmptyState({
  title,
  message,
  action,
}: DetailEmptyStateProps) {
  return (
    <div className="detail-empty">
      <span className="detail-empty-ico" aria-hidden>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="8" cy="8" r="5.5" />
          <path d="M8 7.2v3.5M8 5.2h.01" strokeLinecap="round" />
        </svg>
      </span>
      {title ? <p className="detail-empty-title">{title}</p> : null}
      <p className="detail-empty-msg">{message}</p>
      {action}
    </div>
  )
}

type DetailColumn<T> = {
  key: string
  header: string
  align?: 'left' | 'right'
  render: (row: T) => React.ReactNode
}

type DetailTableProps<T> = {
  columns: DetailColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  emptyTitle?: string
  emptyMessage: string
  emptyAction?: React.ReactNode
  loading?: boolean
  loadingLabel?: string
  actionsHeader?: string
  renderActions?: (row: T) => React.ReactNode
  /** Client-side pagination (default true). */
  paginate?: boolean
  /** Rows per page when paginate is enabled (default 10). */
  pageSize?: number
}

export function DetailTable<T>({
  columns,
  rows,
  rowKey,
  emptyTitle,
  emptyMessage,
  emptyAction,
  loading,
  loadingLabel,
  actionsHeader = 'Action',
  renderActions,
  paginate = true,
  pageSize = 10,
}: DetailTableProps<T>) {
  const pager = useClientPagination(rows, pageSize)
  const visibleRows = paginate ? pager.pageItems : rows

  if (loading) {
    return <TableLoadingState compact label={loadingLabel} />
  }

  if (!rows.length) {
    return (
      <DetailEmptyState
        title={emptyTitle ?? 'Nothing here yet'}
        message={emptyMessage}
        action={emptyAction}
      />
    )
  }

  return (
    <div className="detail-table-shell">
      <div className="detail-table-wrap">
        <table className="detail-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  style={
                    col.align === 'right' ? { textAlign: 'right' } : undefined
                  }
                >
                  {col.header}
                </th>
              ))}
              {renderActions ? (
                <th style={{ textAlign: 'right' }}>{actionsHeader}</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row) => (
              <tr key={rowKey(row)} onClick={onClickableTableRow}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    style={
                      col.align === 'right' ? { textAlign: 'right' } : undefined
                    }
                  >
                    {col.render(row)}
                  </td>
                ))}
                {renderActions ? (
                  <td style={{ textAlign: 'right' }}>
                    <div className="detail-row-actions">
                      {renderActions(row)}
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {paginate && pager.showPager ? (
        <TablePager
          page={pager.page}
          pageSize={pager.pageSize}
          total={pager.total}
          pageCount={pager.pageCount}
          from={pager.from}
          to={pager.to}
          pageSizeOptions={pager.pageSizeOptions}
          onPageChange={pager.setPage}
          onPageSizeChange={pager.setPageSize}
        />
      ) : null}
    </div>
  )
}

type DetailEntityPanelProps = {
  listTitle: string
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  addLabel?: string
  onAdd?: () => void
  canAdd?: boolean
  /** Extra controls next to Add (e.g. Select existing). */
  extraAction?: React.ReactNode
  children: React.ReactNode
}

/** Section bar + body — reuse for any related-entity list inside a detail page. */
export function DetailEntityPanel({
  listTitle,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  addLabel,
  onAdd,
  canAdd = true,
  extraAction,
  children,
}: DetailEntityPanelProps) {
  const addBtn =
    canAdd && onAdd && addLabel ? (
      <DetailButton size="sm" onClick={onAdd}>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
          <path d="M8 3v10M3 8h10" />
        </svg>
        {addLabel}
      </DetailButton>
    ) : null

  const action =
    addBtn || extraAction ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {extraAction}
        {addBtn}
      </span>
    ) : undefined

  return (
    <>
      <DetailSectionBar
        title={listTitle}
        searchPlaceholder={searchPlaceholder}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        action={action}
      />
      {children}
    </>
  )
}

export type { DetailColumn }
